import React from 'react';
import { FileCheck, Search as SearchIcon, Send } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

const HowWeWork = () => {
  const steps = [
    {
      icon: FileCheck,
      number: '01',
      title: 'Information Collection',
      description: 'Share your business details, trademark information, and specific requirements through our simple online form. We gather everything needed to start your application.'
    },
    {
      icon: SearchIcon,
      number: '02',
      title: 'Research and Search Report',
      description: 'Our legal team conducts a comprehensive trademark search to ensure availability. We analyze potential conflicts and provide you with a detailed report and recommendations.'
    },
    {
      icon: Send,
      number: '03',
      title: 'Application and Submission',
      description: 'We prepare and file your complete application with the USPTO. Our experts handle all paperwork, ensuring accuracy and compliance with all legal requirements.'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-[#FDFEFE] via-white to-[#FEF5F5] relative overflow-hidden">
      {/* Decorative grid pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle, #C0392B 1px, transparent 1px)', backgroundSize: '40px 40px'}} />
      </div>
      
      <div className="container mx-auto px-4 max-w-[1320px] relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-4xl md:text-5xl font-bold text-[#2C3E50]">
            How We Work
          </h2>
          <p className="text-xl text-[#2C3E50] max-w-3xl mx-auto">
            Our streamlined three-step process makes trademark registration simple, fast, and stress-free. 
            We handle the complexity so you can focus on growing your business.
          </p>
        </div>

        {/* Steps */}
        <div className="relative">
          {/* Connection Line */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-[#C0392B]/20 via-[#C0392B]/40 to-[#C0392B]/20 transform -translate-y-1/2 z-0" />

          {/* Desktop View - Grid */}
          <div className="hidden lg:grid lg:grid-cols-3 gap-12 relative z-10">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div 
                  key={index}
                  className="relative group"
                >
                  {/* Card */}
                  <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-[#7F8C8D]/10 hover:border-[#C0392B]/50 transition-all duration-500 hover:shadow-2xl card-lift">
                    {/* Number Badge */}
                    <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
                      <div className="w-14 h-14 bg-[#C0392B] text-white rounded-full flex items-center justify-center text-xl font-bold shadow-lg group-hover:scale-125 group-hover:rotate-12 transition-all duration-500 icon-pulse">
                        {step.number}
                      </div>
                    </div>

                    {/* Icon */}
                    <div className="flex justify-center mt-6 mb-6">
                      <div className="w-20 h-20 bg-gradient-to-br from-[#C0392B]/10 to-[#C0392B]/5 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                        <Icon className="w-10 h-10 text-[#C0392B] group-hover:scale-125 transition-transform duration-500" />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="text-center space-y-3">
                      <h3 className="text-2xl font-bold text-[#2C3E50] group-hover:text-[#C0392B] transition-colors duration-300">
                        {step.title}
                      </h3>
                      <p className="text-[#2C3E50] leading-relaxed transition-colors duration-300">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Mobile & Tablet View - Slider */}
          <div className="lg:hidden relative z-10">
            <Swiper
              modules={[Pagination, Autoplay]}
              spaceBetween={20}
              slidesPerView={1}
              breakpoints={{
                640: {
                  slidesPerView: 1,
                },
                768: {
                  slidesPerView: 2,
                }
              }}
              pagination={{ clickable: true }}
              autoplay={{ delay: 3000, disableOnInteraction: false }}
              className="pb-12"
            >
              {steps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <SwiperSlide key={index}>
                    <div className="relative group px-4">
                      {/* Card */}
                      <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-[#7F8C8D]/10 hover:border-[#C0392B]/50 transition-all duration-500 hover:shadow-2xl card-lift">
                        {/* Number Badge */}
                        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
                          <div className="w-14 h-14 bg-[#C0392B] text-white rounded-full flex items-center justify-center text-xl font-bold shadow-lg group-hover:scale-125 group-hover:rotate-12 transition-all duration-500 icon-pulse">
                            {step.number}
                          </div>
                        </div>

                        {/* Icon */}
                        <div className="flex justify-center mt-6 mb-6">
                          <div className="w-20 h-20 bg-gradient-to-br from-[#C0392B]/10 to-[#C0392B]/5 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                            <Icon className="w-10 h-10 text-[#C0392B] group-hover:scale-125 transition-transform duration-500" />
                          </div>
                        </div>

                        {/* Content */}
                        <div className="text-center space-y-3">
                          <h3 className="text-2xl font-bold text-[#2C3E50] group-hover:text-[#C0392B] transition-colors duration-300">
                            {step.title}
                          </h3>
                          <p className="text-[#2C3E50] leading-relaxed transition-colors duration-300">
                            {step.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <p className="text-lg text-[#2C3E50] mb-6">
            Ready to protect your brand? Start your trademark registration today!
          </p>
        </div>
      </div>
    </section>
  );
};

export default HowWeWork;